import React from "react";
import { useHistory } from "react-router-dom";
import "regenerator-runtime/runtime";

import Environment from "services/environment";
import getOriginApplication from "utils/getOriginApplication";
import timeSince from "utils/timeSince";
import { navigateHandler } from "utils/redirection";
import { generateUrl } from "utils/generateUrl";
import { handleLogOut } from "utils/logOut";

import { APPLICATIONS, LINK_TO_ROUTE } from "constants/common";
import { DROPDOWNS, MOE_HEADER } from "../constants";
import { BASE_ASSETS_URL } from "../../../constants/app";
import { trackAnalytics } from "services/analytics";
import { enablePlatformLogin } from "utils/platformUtils";

import styles from "../postLoginHeader.scss";

const showLogout = getOriginApplication() === APPLICATIONS.BROWSER;

const ProfileHeader = ({
  profile,
  profileCompleteness,
  getInitials,
  viewProfile,
  showProfileCompleteness,
  profilePictureUrl,
}) => {
  const completenessCount = showProfileCompleteness ? profileCompleteness.completenessCount : 0;
  return (
    <div className={`${styles.card_header} ${styles.profile_header}`}>
      <div className={styles.profile_icon}>
        <div
          className={styles.progressbar}
          role="progressbar"
          aria-valuenow={profileCompleteness.completenessCount}
          aria-valuemin="0"
          aria-valuemax="100"
          style={{ ["--value"]: completenessCount }}
        >
          {profilePictureUrl ? (
            <img src={profilePictureUrl} alt="Profile" />
          ) : (
            <span>{getInitials(profile.personalInfo?.userName)}</span>
          )}
        </div>
      </div>
      <div className={styles.profile_info}>
        <div className={styles.name}>{profile.personalInfo?.userName}</div>
        <div className={styles.role}>
          <strong>{profile.roleForSelectedLob}</strong>
          <span>Last login - {timeSince(profile.lastLoginTimeStamp)} ago</span>
          {showProfileCompleteness && (
            <p className={styles.complete}>
              {(profileCompleteness.completenessCount > 0 || profileCompleteness.completenessCount === 0) && (
                <span>
                  <span className={styles.percentage_value}>
                    {profileCompleteness.completenessCount}
                    <em>%</em>
                  </span>
                  profile completed
                </span>
              )}
              <span className={styles.link} onClick={viewProfile}>
                view
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Profile({
  source,
  profile,
  onLogout,
  activeDropdown,
  setActiveDropdown,
  profileCompleteness,
  lobDetails,
  partyDetails,
  showProfileCompleteness,
  profilePictureUrl,
}) {
  const env = Environment.getEnv();
  const history = useHistory();

  const faOnLinkClick = (name) => {
    trackAnalytics({
      eVar9: `b2b|aeh|my account|${name.toLowerCase()}`,
    });
  };

  const clearStorage = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("trackOrdertoken"); // for track order journey (Data)
    localStorage.removeItem("trackOrdercpqOrderId"); // for track order journey (Data)
    localStorage.removeItem("s-iv-user"); // for ReadOnly Super Admin journey
    sessionStorage.clear();
  };

  const onLogOutClick = () => {
    faOnLinkClick("logout");
    const isPlatformEnabled = enablePlatformLogin();
    if (isPlatformEnabled) {
      window.location.href = env.selfcareLoginPageBaseUrl + "logout";
    } else {
      handleLogOut(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_LOGOUT_CLICK`, onLogout);
    }
  };

  const navigateTo = (path) => {
    const url = generateUrl(path, { lob: lobDetails?.key }, { partyId: partyDetails?.partyId });

    navigateHandler({ history, source, path: url });
  };

  const handleManageAccountClick = () => {
    faOnLinkClick("manage account");
    trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_MANAGE_ACCOUNT_CLICK`).finally(() =>
      navigateTo(LINK_TO_ROUTE.MANAGE_ACCOUNT),
    );
  };

  const handleMyOrderClick = () => {
    faOnLinkClick("track my order");
    trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_TRACK_ORDER_CLICK`).finally(() => navigateTo(LINK_TO_ROUTE.TRACK_ORDER));
  };

  const handleChangePasswordClick = (e) => {
    e.preventDefault();
    faOnLinkClick("change password");
    trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_CHANGE_PASSWORD_CLICK`).finally(() =>
      navigateTo(LINK_TO_ROUTE.CHANGE_PASSWORD),
    );
  };

  const handleViewAllActivityClick = (e) => {
    e.preventDefault();
    faOnLinkClick("all activity");
    trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.PROFILE}_ALL_ACTIVITY_CLICK`).finally(() => navigateTo(LINK_TO_ROUTE.USER_ACTIVITY));
  };

  const getInitials = (string) => {
    if (!string) return;
    let names = string.split(" ");
    let initials = names[0].substring(0, 1).toUpperCase();

    if (names.length > 1) {
      initials += names[names.length - 1].substring(0, 1).toUpperCase();
    }
    return initials;
  };

  const viewProfile = () => {
    navigateTo(LINK_TO_ROUTE.USER_PROFILE);
  };

  return (
    <li
      onClick={() => setActiveDropdown(DROPDOWNS.PROFILE)}
      className={`${styles.drop_down} ${styles.user_login} ${activeDropdown === DROPDOWNS.PROFILE ? styles.active : ""}`}
    >
      <span>
        <img src={`${BASE_ASSETS_URL}images/user1.svg`} alt="Profile" width={"24px"} height={"24px"} />
      </span>
      <div className={styles.dropbox}>
        <div className={`${styles.dropdown_card} ${styles.profile_card}`}>
          <ProfileHeader
            {...{ profile, profileCompleteness, getInitials, viewProfile, showProfileCompleteness, profilePictureUrl }}
          />

          <div className={`${styles.card_content} ${styles.profile_content}`}>
            <div onClick={handleManageAccountClick} className={styles.account_info}>
              <h5>
                Manage Account <span className={styles.arrowright}></span>
              </h5>
              <p>You can change your password, add user and edit personal details here. </p>
            </div>
            <div onClick={handleMyOrderClick} className={styles.account_info}>
              <h5>
                My Orders <span className={styles.arrowright}></span>
              </h5>
              <p>Manage your orders or raise a repeat request from here.</p>
            </div>
            <ul className={styles.profile_link}>
              <li>
                <a role="button" onClick={handleViewAllActivityClick}>
                  Recent Activity
                </a>
              </li>
              <li>
                <a role="button" onClick={handleChangePasswordClick}>
                  Change Password
                </a>
              </li>
            </ul>
          </div>
          {showLogout && (
            <div className={styles.cord_footer}>
              <div className={styles.logout_area}>
                Are you done for today?&nbsp;
                <span onClick={onLogOutClick} className={styles.logout}>
                  Logout
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
